import { useMemo } from 'react'
import type { TRichMenuBounds, TRichMenuTemplate } from 'types'

type RichMenuTemplateThumbnailProps = {
  template: TRichMenuTemplate
  isSelected: boolean
  onClick: () => void
  labelJa: string
}

const cell =
  'rounded-[1px] border border-neutral-400 bg-neutral-200/90 box-border'

function BoundsWireframe({
  size,
  bounds,
}: {
  size: { width: number; height: number }
  bounds: TRichMenuBounds[]
}) {
  const { width: W, height: H } = size
  return (
    <div className="relative h-full w-full">
      {bounds.map((b, i) => (
        <div
          key={i}
          className={`absolute ${cell}`}
          style={{
            left: `${(b.x / W) * 100}%`,
            top: `${(b.y / H) * 100}%`,
            width: `${(b.width / W) * 100}%`,
            height: `${(b.height / H) * 100}%`,
          }}
        />
      ))}
    </div>
  )
}

function GridWireframe({ columns, rows }: { columns: number; rows: number }) {
  return (
    <div
      className="grid h-full w-full gap-px p-px"
      style={{
        gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
        gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`,
      }}
    >
      {Array.from({ length: columns * rows }).map((_, i) => (
        <div key={i} className={cell} />
      ))}
    </div>
  )
}

export function RichMenuTemplateThumbnail({
  template,
  isSelected,
  onClick,
  labelJa,
}: RichMenuTemplateThumbnailProps) {
  const aspectStyle = {
    aspectRatio: `${template.size.width} / ${template.size.height}`,
  }

  const areaCount = template.areaKeys.length
  const useBounds =
    template.bounds.length > 0 && template.bounds.length === template.areaKeys.length

  const wireframe = useMemo(() => useBounds ? (
    <BoundsWireframe size={template.size} bounds={template.bounds} />
  ) : areaCount <= 1 ? (
    <div className={`h-full w-full ${cell}`} />
  ) : areaCount === 2 ? (
    <GridWireframe columns={2} rows={1} />
  ) : areaCount === 3 ? (
    <GridWireframe columns={3} rows={1} />
  ) : areaCount === 4 ? (
    <GridWireframe columns={2} rows={2} />
  ) : areaCount === 6 ? (
    <GridWireframe columns={3} rows={2} />
  ) : (
    <div className={`h-full w-full ${cell}`} />
  ), [
    template.size,
    template.bounds,
    areaCount,
    useBounds,
  ])

  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-[104px] flex-col items-center gap-2 rounded-lg transition ${isSelected
        ? 'ring-2 ring-emerald-500 ring-offset-2 ring-offset-white'
        : 'hover:opacity-90'
        }`}
    >
      <div
        className="w-full overflow-hidden rounded border border-neutral-300 bg-neutral-100 p-0.5 shadow-inner"
        style={aspectStyle}
      >
        <div className="h-full w-full">{wireframe}</div>
      </div>
      <span className="max-w-[104px] text-center text-[10px] leading-tight text-neutral-700">
        {labelJa}
      </span>
    </button>
  )
}
